import type { EditorAPI } from '../../types';
import { useEffect, useRef, useState } from 'react';
import { getSelectedNodeLocalId } from '../../utils/getSelectedNodeLocalId';
import type { ForgeExtensionParameters } from '../../types/extension';

interface UseGetStaticForgeDocProps {
	editorAPI?: EditorAPI;
	extensionParameters: ForgeExtensionParameters;
	macroConfig?: Record<string, unknown>;
}

const RUNTIME_UNMOUNT_TIMEOUT = 1000;

// Determines if the runtime should be rendered for the static macro and if not, returns the currently stored staticForgeDoc
export const useGetStaticForgeDoc = ({
	editorAPI,
	extensionParameters,
	macroConfig,
}: UseGetStaticForgeDocProps) => {
	const isStaticMacro =
		extensionParameters?.extensionProperties?.extension?.properties?.content === 'static';

	const previousCustomConfigRef = useRef<Record<string, unknown> | null | undefined>(null);

	const [shouldRenderRuntime, setShouldRenderRuntime] = useState(isStaticMacro ? false : true);

	const gqlExtension = extensionParameters?.extensionProperties?.extension;
	const hasCustomConfig = !!gqlExtension?.properties?.config?.resource;

	// Handles static macros with classic macro config
	useEffect(() => {
		if (!isStaticMacro || hasCustomConfig || !editorAPI?.extension?.sharedState) {
			return;
		}

		const unsubscribe = editorAPI.extension.sharedState.onChange(
			({ nextSharedState, prevSharedState }) => {
				const prevIsOpen = prevSharedState?.showContextPanel;
				const currIsOpen = nextSharedState?.showContextPanel;
				// This listener is currently set to all macros with a classic config
				// Check if the selected node is the same as the static macro
				const selectedNodeLocalId = getSelectedNodeLocalId(editorAPI) || null;
				const isSelected = selectedNodeLocalId === extensionParameters?.localId;

				// Context panel was just opened
				// Runtime must be mounted
				if (isSelected) {
					if (!prevIsOpen && currIsOpen) {
						setShouldRenderRuntime(true);
					}
				}
				// If the context panel is closed, the runtime is unmounted.
				// Any changes to the config will be applied via the useEffect below
				// Handles context panel opening and closing without any changes to the config being made
				if (prevIsOpen && !currIsOpen) {
					setShouldRenderRuntime(false);
				}
			},
		);

		return () => {
			unsubscribe();
		};
	}, [editorAPI, isStaticMacro, hasCustomConfig, extensionParameters?.localId]);

	useEffect(() => {
		// Watch for changes to the config
		// Mounts the runtime briefly to allow the config to be applied to the main macro before unmounting again
		if (previousCustomConfigRef.current !== macroConfig) {
			previousCustomConfigRef.current = macroConfig;
			setShouldRenderRuntime(true);
			// Remove the runtime after 1 second to allow after it finishes updating the main macro with the updated config
			setTimeout(() => {
				setShouldRenderRuntime(false);
			}, RUNTIME_UNMOUNT_TIMEOUT);
		}
	}, [macroConfig, extensionParameters?.localId]);

	return {
		shouldRenderRuntime,
		staticForgeDoc: extensionParameters?.staticForgeDoc || null,
	};
};
